/**
 * lib/templates.ts
 * ------------------------------------------------------------------
 * Email templates + branded-HTML rendering for the comms hub.
 * Templates pre-fill the subject and (plain-text) body. On send, the
 * body is wrapped by renderBrandedHtml() — the School Portal shell for
 * `school-` templates, Acepsle's orange-banner shell otherwise — so the
 * sent HTML always reflects edits.
 *
 * Placeholders:
 *   {{recipientName}} — defaults to "<schoolName> Team", else "there"
 *   {{senderName}}    — defaults to "Simon Madega"
 *   {{companyName}}   — defaults to "AcePSLE (PTY) Ltd"
 *   {{schoolName}}    — defaults to "your school"
 *   {{proposalUrl}}   — defaults to the proposals index
 *
 * The proposal templates below name the product "School Portal" and
 * sign off as "AcePSLE (PTY) Ltd" — the trading name used on the
 * proposal documents and the company page. Deliberately not
 * {{companyName}}, so an emailed proposal and the document it links to
 * carry the same names.
 * ------------------------------------------------------------------
 */
export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  description: string;
}

const ORANGE = '#FF6B35';
const CHARCOAL = '#1F2937';
const GRAY = '#6B7280';
const LIGHT_BG = '#F8F9FB';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/*
 * School Portal emails wear the product's look rather than the company's, so
 * an email and the pitch it links to read as one thing: the palette measured
 * from the printed pages, the logo, the blue-green wave, a navy footer.
 *
 * Images are hosted, not attached: a CID attachment shows up as a stray file
 * in several clients. The wave is a PNG rather than inline SVG because Gmail
 * strips SVG from email. The older orange mark at /images/schoolportal-web.png
 * is superseded.
 *
 * DARK MODE. Every image is transparent, and there are two logos, because the
 * wordmark's "School" is dark navy and vanishes on a dark screen:
 *   - schoolportal-email.png      navy/green with a crisp 1px white outline.
 *     Identical to the plain logo on white; still legible where a client
 *     darkens the email WITHOUT telling it (the Gmail apps), since no CSS can
 *     react there.
 *   - schoolportal-email-dark.png white lettering, green kept. Swapped in by
 *     the prefers-color-scheme / [data-ogsc] rules below, which Apple Mail and
 *     the Outlook apps honour, along with dark card and text colours.
 *
 * The ?v= query is a cache key, not decoration: Gmail's image proxy caches by
 * URL, so replacing a file under the same address can keep showing the old one.
 * Bump it whenever an image here is replaced.
 */
/*
 * Keep in step with BRAND.tagline in public/pitches/schoolportal/content.js.
 * It cannot be imported: that file ships as a static asset the pitch loads in
 * the browser, not as part of this app's bundle. Two places, one string.
 */
const SP_TAGLINE = 'Built to make schools work better.';

const SP_ASSET_VERSION = '3';  // wordmark-only logos (tagline removed)
const SP_LOGO_URL = `https://hub.acepslebotswana.com/images/schoolportal-email.png?v=${SP_ASSET_VERSION}`;
const SP_LOGO_DARK_URL = `https://hub.acepslebotswana.com/images/schoolportal-email-dark.png?v=${SP_ASSET_VERSION}`;
const SP_WAVE_URL = `https://hub.acepslebotswana.com/images/schoolportal-email-wave.png?v=${SP_ASSET_VERSION}`;
const SP_NAVY = '#002F90';
const SP_BLUE = '#003FB0';
const SP_GREEN = '#2CAA34';
const SP_INK = '#15254D';
const SP_WASH = '#F2F8FF';

export type EmailTheme = 'acepsle' | 'schoolportal';

export interface BrandedHtmlOptions {
  /** 'schoolportal' for School Portal emails; the orange Acepsle shell otherwise. */
  theme?: EmailTheme;
}

/** The School Portal shell — navy bar, logo, wave, navy footer. */
function schoolPortalShell(content: string): string {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>School Portal</title>
<meta name="color-scheme" content="light dark">
<meta name="supported-color-schemes" content="light dark">
<style>
:root { color-scheme: light dark; supported-color-schemes: light dark; }
@media (prefers-color-scheme: dark) {
  .sp-bg { background-color: #0A1330 !important; }
  .sp-card { background-color: #111C3D !important; box-shadow: none !important; }
  .sp-text, .sp-text p { color: #E3EAF7 !important; }
  .sp-link { color: #8DB8FF !important; }
  .sp-btn { background-color: #1553D6 !important; }
  .sp-tag { color: #6FD98A !important; }
  .sp-logo-light { display: none !important; }
  .sp-logo-dark { display: block !important; max-height: none !important; overflow: visible !important; }
}
[data-ogsc] .sp-text, [data-ogsc] .sp-text p { color: #E3EAF7 !important; }
[data-ogsc] .sp-link { color: #8DB8FF !important; }
[data-ogsc] .sp-tag { color: #6FD98A !important; }
[data-ogsc] .sp-logo-light { display: none !important; }
[data-ogsc] .sp-logo-dark { display: block !important; max-height: none !important; overflow: visible !important; }
[data-ogsb] .sp-bg { background-color: #0A1330 !important; }
[data-ogsb] .sp-card { background-color: #111C3D !important; }
[data-ogsb] .sp-btn { background-color: #1553D6 !important; }
</style>
</head>
<body class="sp-bg" style="margin:0;padding:0;background-color:${SP_WASH};font-family:Arial,Helvetica,sans-serif;color:${SP_INK};line-height:1.6;">
<table class="sp-bg" role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:${SP_WASH};padding:24px 0;">
<tr><td align="center">
<table class="sp-card" role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 2px 10px rgba(0,31,102,0.07);">
<tr><td style="height:6px;background-color:${SP_NAVY};font-size:0;line-height:0;">&nbsp;</td></tr>
<tr>
<td class="sp-card" style="padding:30px 40px 0;background-color:#ffffff;">
<img class="sp-logo-light" src="${SP_LOGO_URL}" width="238" alt="School Portal" style="display:block;width:238px;max-width:100%;height:auto;border:0;margin-left:-9px;">
<!--[if !mso]><!-->
<div class="sp-logo-dark" style="display:none;max-height:0;overflow:hidden;mso-hide:all;">
<img src="${SP_LOGO_DARK_URL}" width="238" alt="School Portal" style="display:block;width:238px;max-width:100%;height:auto;border:0;margin-left:-9px;">
</div>
<!--<![endif]-->
<div class="sp-tag" style="margin-top:12px;color:${SP_GREEN};font-size:11px;font-weight:bold;letter-spacing:1.6px;text-transform:uppercase;">${SP_TAGLINE}</div>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-top:12px;"><tr><td style="width:70px;height:5px;background-color:${SP_GREEN};border-radius:5px;font-size:0;line-height:0;">&nbsp;</td></tr></table>
</td>
</tr>
<tr>
<td class="sp-card sp-text" style="padding:28px 40px 14px;background-color:#ffffff;color:${SP_INK};font-size:15px;line-height:1.65;">
${content}
</td>
</tr>
<tr><td class="sp-card" style="padding:0;font-size:0;line-height:0;background-color:#ffffff;"><img src="${SP_WAVE_URL}" width="600" alt="" style="display:block;width:100%;height:auto;border:0;"></td></tr>
<tr>
<td style="padding:18px 40px 22px;background-color:${SP_NAVY};color:#C9D8F5;font-size:12px;line-height:1.6;">
<strong style="color:#ffffff;">School Portal</strong> &middot; designed and built by AcePSLE (PTY) Ltd<br>
<a href="mailto:info@acepslebotswana.com" style="color:#9BE08E;text-decoration:none;">info@acepslebotswana.com</a> &nbsp;&middot;&nbsp; +267 73 453 092
</td>
</tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

/** Wrap content in the shared branded shell — orange banner + footer. */
function htmlShell(content: string, opts: BrandedHtmlOptions = {}): string {
  if (opts.theme === 'schoolportal') return schoolPortalShell(content);
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>AcePSLE (PTY) Ltd</title>
</head>
<body style="margin:0;padding:0;background-color:${LIGHT_BG};font-family:Arial,Helvetica,sans-serif;color:${CHARCOAL};line-height:1.6;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:${LIGHT_BG};padding:24px 0;">
<tr><td align="center">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.04);">
<tr>
<td style="background-color:${ORANGE};padding:28px 40px;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="padding-right:10px;vertical-align:middle;">
<div style="color:#ffffff;font-size:30px;line-height:1;font-weight:700;font-family:Arial,Helvetica,sans-serif;">&#10095;</div>
</td>
<td style="vertical-align:middle;">
<div style="color:#ffffff;font-size:28px;font-weight:600;letter-spacing:-0.5px;line-height:1;">AcePSLE</div>
<div style="color:#ffffff;font-size:10px;letter-spacing:2px;margin-top:5px;opacity:0.9;">(PTY)&nbsp;&nbsp;LTD</div>
</td>
</tr>
</table>
</td>
</tr>
<tr>
<td style="padding:36px 40px;color:${CHARCOAL};font-size:15px;line-height:1.65;">
${content}
</td>
</tr>
<tr>
<td style="padding:18px 40px;background-color:#fafafa;color:${GRAY};font-size:12px;line-height:1.5;border-top:1px solid #eee;">
<strong style="color:${CHARCOAL};">AcePSLE (PTY) Ltd</strong><br>
<a href="mailto:info@acepslebotswana.com" style="color:${ORANGE};text-decoration:none;">info@acepslebotswana.com</a> &nbsp;&middot;&nbsp; +267 73 453 092
</td>
</tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

/** Turn a plain-text body into branded HTML (paragraphs on blank lines). */
export function renderBrandedHtml(
  body: string,
  opts: BrandedHtmlOptions = {},
): string {
  const sp = opts.theme === 'schoolportal';
  const paras = body
    .split(/\n{2,}/)
    .map((p) => {
      const button = sp ? ctaButton(p) : null;
      if (button) return button;
      const html = linkify(escapeHtml(p), sp ? SP_BLUE : ORANGE, sp ? 'sp-link' : '').replace(/\n/g, '<br>');
      return `<p style="margin:0 0 16px;">${html}</p>`;
    })
    .join('\n');
  return htmlShell(paras, opts);
}

/**
 * On School Portal emails, a paragraph that is exactly one line of
 * "Label: https://…" becomes a button. The plain-text copy of the same email
 * still reads naturally ("View the presentation: https://…"), so no markup has
 * to be typed into the composer. Returns null for any other paragraph.
 */
function ctaButton(paragraph: string): string | null {
  const m = paragraph.trim().match(/^([^\n:]{3,60}):\s+(https:\/\/[^\s<>"]+)$/);
  if (!m) return null;
  const label = escapeHtml(m[1].trim());
  const href = escapeHtml(m[2]);
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:6px 0 22px;"><tr>
<td class="sp-btn" style="background-color:${SP_NAVY};border-radius:999px;">
<a href="${href}" style="display:inline-block;padding:13px 26px;color:#ffffff;font-weight:bold;font-size:15px;text-decoration:none;border-radius:999px;">${label} &rarr;</a>
</td></tr></table>`;
}

/**
 * Make bare https:// URLs clickable. Runs on ALREADY-escaped text, so a URL
 * cannot inject markup. Trailing sentence punctuation stays outside the link:
 * "see https://example.com." must not link to "example.com.".
 */
function linkify(escaped: string, color: string, cls = ''): string {
  const klass = cls ? ` class="${cls}"` : '';
  return escaped.replace(/https:\/\/[^\s<]+/g, (url) => {
    const trail = url.match(/[.,;:!?)]+$/)?.[0] ?? '';
    const href = trail ? url.slice(0, -trail.length) : url;
    return `<a${klass} href="${href}" style="color:${color};text-decoration:underline;">${href}</a>${trail}`;
  });
}

export const EMAIL_TEMPLATES: EmailTemplate[] = [
  { id: 'blank', name: 'Blank message', description: 'Start from scratch', subject: '', body: '' },
  {
    id: 'intro',
    name: 'Introduction / cover note',
    description: 'Introduce Acepsle and what you do',
    subject: 'Acepsle Proprietary Limited — software solutions',
    body: `Dear {{recipientName}},

Thank you for your interest in Acepsle Proprietary Limited. We build modern, tax-ready business software for organisations in Botswana — from invoicing and finance hubs to full management platforms and custom systems.

I would be glad to discuss how we can help, and to answer any questions you may have.

Kind regards,
{{senderName}}
{{companyName}}`,
  },
  {
    id: 'follow-up',
    name: 'Follow-up',
    description: 'Gentle nudge after no reply',
    subject: 'Following up — Acepsle Proprietary Limited',
    body: `Dear {{recipientName}},

I hope this finds you well. I am following up on my previous message to see whether you had any questions, or would like to arrange a time to talk.

I remain happy to help whenever the timing suits you.

Kind regards,
{{senderName}}
{{companyName}}`,
  },
  {
    id: 'thank-you',
    name: 'Thank you / acknowledgement',
    description: 'Acknowledge a payment, meeting, or reply',
    subject: 'Thank you — Acepsle Proprietary Limited',
    body: `Dear {{recipientName}},

Thank you very much — this is gratefully received and confirmed on our side.

Please do not hesitate to reach out if there is anything further you need.

Kind regards,
{{senderName}}
{{companyName}}`,
  },
  {
    id: 'school-proposal',
    name: 'School Portal — introduction',
    description: 'Introduces School Portal, links the proposal, offers a demonstration',
    subject: 'School Portal — a school management system for {{schoolName}}',
    body: `Dear {{recipientName}},

I am the lead developer at AcePSLE (PTY) Ltd, the company behind School Portal, a school management system we would like {{schoolName}} to consider for the management of your school.

School Portal brings the core functions of a school together — academics and reporting, attendance, admissions, parent access, HR and payroll, and finance and accounting.

I have prepared a proposal for {{schoolName}} that introduces us, explains the system in more detail, and outlines how we would approach moving from an existing system.

You can view it here:

{{proposalUrl}}

I have also attached the same proposal as a PDF for easy printing or circulation.

If what you see is of interest, we would be glad to come to the school and demonstrate the live system to your team. There is no cost or commitment — it would simply be an opportunity for you to see School Portal working and decide whether it is worth taking the conversation further.

Please feel free to suggest a convenient day and time, and we will arrange around you.

Kind regards,

{{senderName}}
Head of Development
AcePSLE (PTY) Ltd
info@acepslebotswana.com · +267 73 453 092`,
  },
  {
    id: 'school-proposal-followup',
    name: 'School Portal — follow-up',
    description: 'Nudge after a proposal has been sent',
    subject: 'Following up — School Portal for {{schoolName}}',
    body: `Dear {{recipientName}},

I hope the term is going well. I am following up on the School Portal proposal I sent for {{schoolName}}, in case any questions have come up since.

If it would help to see the system rather than read about it, the offer stands: we will present the live platform at whatever time suits you, in person or online, and generate report cards in front of you from real data. There is no cost and no commitment attached.

The proposal remains available here:

{{proposalUrl}}

Kind regards,

{{senderName}}
Head of Development
AcePSLE (PTY) Ltd
info@acepslebotswana.com · +267 73 453 092`,
  },
  {
    id: 'school-demo-confirmation',
    name: 'School Portal — confirm a demonstration',
    description: 'Confirm a booked live presentation and set expectations',
    subject: 'Confirmed — School Portal demonstration for {{schoolName}}',
    body: `Dear {{recipientName}},

Thank you — the demonstration is confirmed. We will walk your team through School Portal running live, with real data.

It usually takes about forty-five minutes. We will focus on the parts most relevant to {{schoolName}}, and we would rather you interrupt with the awkward cases than sit through a script. If your finance or administrative staff can join for the second half, that is worth arranging.

Nothing needs to be installed or prepared on your side. A screen or projector is helpful but not essential.

If anything changes, just let us know — we can move it at short notice.

Kind regards,

{{senderName}}
Head of Development
AcePSLE (PTY) Ltd
info@acepslebotswana.com · +267 73 453 092`,
  },
  {
    /*
      Written for Al-Nur after the 9 September 2026 meeting, to accompany
      ALNUR-PROP-2026-002 (attach the PDF). Specific on purpose rather than a
      generic template: it names a meeting, two references and a validity date.

      Keep the careful wording. "Without over-committing" is true of the
      LICENCE (5 months, not 12), the ACCOUNTING PACKAGE (trial) and the 2027
      DECISIONS (deferred to 14 December) — but the setup fee is payable in
      full, and the proposal adds a P 7,000 accounting setup charge that was not
      in the original. The email says both, so a board member does not read
      "try it out" and then find P 60,083.33 payable.
    */
    id: 'school-staggered-alnur',
    name: 'School Portal — staggered implementation (Al-Nur)',
    description: 'Covers ALNUR-PROP-2026-002 after the 9 Sept meeting — attach the PDF',
    subject: 'Al-Nur School — proposal for a staggered implementation of School Portal',
    body: `Dear Mohammed,

Thank you for your time at our meeting on 9 September.

As a follow-up, please find attached a proposal for implementing our original commercial proposal (ALNUR-PROP-2026-001, 24 June 2026) in a staggered way. It is designed to let the school put the system to work and experience what it can do, without committing further than it needs to before it has seen that for itself.

We offer it on this basis for a simple reason. A system like ours is fully capable, but it is newer than the long-established brands, and a school considering it will reasonably want to be satisfied that it matches, and goes beyond, what those systems offer. A staggered start gives the school the time to reach that judgement on its own evidence.

In summary, the attached proposal (ALNUR-PROP-2026-002):

• charges the platform licence only from 1 August 2026, prorated to 31 December — five months rather than a full year;
• makes the accounting package available on trial, at no licence fee and with no obligation to continue, until 31 December 2026;
• spreads payment across an amount on acceptance and three monthly instalments to the end of November;
• leaves the two larger decisions — renewing the licence for 2027, and whether to adopt the accounting package — to 14 December 2026, to be taken separately.

The setup fee and the annual licence rate are unchanged from the original proposal. The one addition is a one-time charge for setting up the accounting features, which were not part of the original scope. The proposal is valid until 30 September 2026.

We have also chosen a name for the system: it is now called School Portal. Alongside the proposal, we have prepared a presentation that walks through what School Portal does for each role in a school, from the boardroom to the classroom.

View the School Portal presentation: https://hub.acepslebotswana.com/pitches/schoolportal/

We would be glad to answer any questions, or to meet again with you or the board at a time that suits.

Kind regards,
{{senderName}}`,
  },
];

export function getTemplate(id: string): EmailTemplate | undefined {
  return EMAIL_TEMPLATES.find((t) => t.id === id);
}

export interface TemplateContext {
  recipientName?: string;
  senderName?: string;
  companyName?: string;
  /** The organisation being pitched, e.g. "The Learning Centre". */
  schoolName?: string;
  /** Link to that organisation's hosted proposal page. */
  proposalUrl?: string;
}

export function substitute(text: string, ctx: TemplateContext): string {
  // With no explicit recipient, a named organisation gives a better
  // greeting than "there" — "Dear The Learning Centre Team,".
  const school = ctx.schoolName?.trim();
  const recipient =
    ctx.recipientName?.trim() || (school ? `${school} Team` : 'there');
  return text
    .replaceAll('{{recipientName}}', recipient)
    .replaceAll('{{senderName}}', ctx.senderName?.trim() || 'Simon Madega')
    .replaceAll('{{companyName}}', ctx.companyName?.trim() || 'AcePSLE (PTY) Ltd')
    .replaceAll('{{schoolName}}', ctx.schoolName?.trim() || 'your school')
    .replaceAll(
      '{{proposalUrl}}',
      ctx.proposalUrl?.trim() || 'https://hub.acepslebotswana.com/proposals/',
    );
}
